import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import { ThunkExtra } from '@app/store';
import {
  EventTypeService,
  GetCollectionResponse,
  GetCollectionsEventTypesResponse,
  GetEventTypesByCollectionParams,
} from '@app/gen/event-type';

import { ProcessCatalogState } from '../processCatalog.state';
import { EventItemType } from '../../types';

export const setSelectedEventType = createAction<EventItemType | null>(
  'processCatalog/setSelectedEventType',
);

export const getProcessEventTypes = createAsyncThunk<
  GetCollectionsEventTypesResponse,
  GetEventTypesByCollectionParams,
  ThunkExtra
>(
  'processCatalog/getProcessEventTypes',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventTypeService = inject(EventTypeService);
      const result = await eventTypeService.getEventTypesByCollection({
        params,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getProcessCollections = createAsyncThunk<
  GetCollectionResponse,
  undefined,
  ThunkExtra
>(
  'processCatalog/getProcessCollections',
  async (_, { rejectWithValue, extra: { inject }, dispatch }) => {
    try {
      const eventTypeService = inject(EventTypeService);
      const result = await eventTypeService.getCollections();

      if (result.collections.length > 0) {
        await dispatch(
          getProcessEventTypes({
            collection_id: result.collections[0].id,
          }),
        );
      }

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const processCatalogReducers = (
  builder: ActionReducerMapBuilder<ProcessCatalogState>,
) => {
  builder.addCase(setSelectedEventType, (state, { payload }) => {
    state.eventTypes.selectedEventType = payload;
  });
  builder.addCase(getProcessCollections.pending, (state) => {
    state.collections.status = 'loading';
    state.collections.error = null;
  });
  builder.addCase(getProcessCollections.fulfilled, (state, { payload }) => {
    state.collections.status = 'complete';
    state.collections.items = payload.collections;
    state.collections.error = null;
  });
  builder.addCase(
    getProcessCollections.rejected,
    (state, { payload, error }) => {
      state.collections.status = 'failed';
      state.collections.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
  builder.addCase(getProcessEventTypes.pending, (state) => {
    state.eventTypes.status = 'loading';
    state.eventTypes.error = null;
  });
  builder.addCase(getProcessEventTypes.fulfilled, (state, { payload }) => {
    state.eventTypes.status = 'complete';
    state.eventTypes.error = null;
    state.eventTypes.items = payload.event_types || [];
    state.eventTypes.collection = payload.collection;
  });
  builder.addCase(
    getProcessEventTypes.rejected,
    (state, { payload, error }) => {
      state.eventTypes.status = 'failed';
      state.eventTypes.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
    },
  );
};
